import { Controller } from "stimulus"

/**
 * Loads position description templates into the posting description field.
 *
 * When the position select changes, fetches available description templates
 * for that position's type. User can pick a template and click "Use as Template"
 * to populate the Trix editor.
 *
 * Usage:
 *   <div data-controller="description-template-loader"
 *        data-description-template-loader-url="/hr/postings/description_templates">
 *     <select data-target="description-template-loader.positionSelect"
 *             data-action="change->description-template-loader#positionChanged">
 *     <div data-target="description-template-loader.templateSection">
 *       <select data-target="description-template-loader.templateSelect">
 *       <button data-action="click->description-template-loader#applyTemplate">
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["positionSelect", "templateSection", "templateSelect", "descriptionEditor"]

  connect() {
    this.templates = []
    this.hideTemplateSection()

    if (this.positionSelectTarget.value) {
      this.positionChanged()
    }
  }

  async positionChanged() {
    const positionId = this.positionSelectTarget.value
    if (!positionId) {
      this.hideTemplateSection()
      return
    }

    try {
      const url = this.data.get("url")
      const response = await fetch(`${url}?position_id=${positionId}`, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (!response.ok) {
        this.templates = []
        this.hideTemplateSection()
        return
      }

      this.templates = await response.json()

      if (this.templates.length > 0) {
        this.populateTemplateSelect()
        this.showTemplateSection()
      } else {
        this.hideTemplateSection()
      }
    } catch (e) {
      console.error("Failed to fetch description templates", e)
      this.hideTemplateSection()
    }
  }

  applyTemplate(event) {
    event.preventDefault()

    const selectedId = parseInt(this.templateSelectTarget.value)
    if (!selectedId) return

    const template = this.templates.find(t => t.id === selectedId)
    if (!template) return

    const editor = this.descriptionEditorTarget
    const trixEditor = editor.querySelector("trix-editor")
    if (!trixEditor || !trixEditor.editor) return

    if (trixEditor.editor.getDocument().toString().trim().length > 0) {
      if (!confirm("This will replace the current description. Continue?")) return
    }

    trixEditor.editor.loadHTML(template.content)
  }

  populateTemplateSelect() {
    const select = this.templateSelectTarget
    select.innerHTML = '<option value="">Select a template...</option>'

    this.templates.forEach(template => {
      const option = document.createElement("option")
      option.value = template.id
      option.textContent = template.title
      select.appendChild(option)
    })
  }

  showTemplateSection() {
    this.templateSectionTarget.classList.remove("hidden")
  }

  hideTemplateSection() {
    if (this.hasTemplateSectionTarget) {
      this.templateSectionTarget.classList.add("hidden")
    }
  }
}
